import { type ReactNode, useEffect, useRef } from "react";
import { initPostHog } from "./posthog-adapter.js";
import type { AnalyticsConfig, AnalyticsProps, AnalyticsValue } from "./types.js";
import { __setPlatform, track } from "./wrapper.js";

interface CakewalkAnalyticsEventDetail {
  readonly event: string;
  readonly props?: AnalyticsProps;
}

interface CakewalkApiErrorDetail {
  readonly status?: number;
  readonly url?: string;
  readonly request_id?: string;
  readonly code?: string;
}

interface CakewalkErrorBoundaryDetail {
  readonly message: string;
  readonly name?: string;
  readonly componentStack?: string;
}

export interface AnalyticsProviderProps {
  readonly config: AnalyticsConfig;
  readonly children: ReactNode;
}

/**
 * Initialises PostHog and registers a single `window` listener for the
 * three `cakewalk:*` `CustomEvent`s that other packages (`frontend-auth`,
 * `frontend-api`, `@cakewalk/ui`'s `<ErrorBoundary>`) dispatch. This is
 * the chokepoint that the analytics rule (`cross-cutting/analytics.mdc`)
 * mandates — every app mounts ONE of these at the composition root.
 */
export function AnalyticsProvider({ config, children }: AnalyticsProviderProps) {
  const initialised = useRef(false);

  useEffect(() => {
    if (initialised.current) return;
    initialised.current = true;
    __setPlatform(config.platform);
    initPostHog(config);

    const handleEvent = (e: Event): void => {
      const detail = (e as CustomEvent<CakewalkAnalyticsEventDetail>).detail;
      if (!detail?.event) return;
      track(detail.event, detail.props ?? {});
    };
    const handleApiError = (e: Event): void => {
      const detail = (e as CustomEvent<CakewalkApiErrorDetail>).detail;
      if (!detail) return;
      const props: Record<string, AnalyticsValue> = {};
      if (detail.status !== undefined) props.status = detail.status;
      if (detail.url) props.url = detail.url;
      if (detail.request_id) props.request_id = detail.request_id;
      if (detail.code) props.code = detail.code;
      track("api_error", props);
    };
    const handleBoundaryError = (e: Event): void => {
      const detail = (e as CustomEvent<CakewalkErrorBoundaryDetail>).detail;
      if (!detail) return;
      track("ui_error_boundary", {
        error_name: detail.name ?? "Error",
        error_message: detail.message,
      });
    };

    window.addEventListener("cakewalk:analytics-event", handleEvent);
    window.addEventListener("cakewalk:api-error", handleApiError);
    window.addEventListener("cakewalk:error-boundary", handleBoundaryError);

    return () => {
      window.removeEventListener("cakewalk:analytics-event", handleEvent);
      window.removeEventListener("cakewalk:api-error", handleApiError);
      window.removeEventListener("cakewalk:error-boundary", handleBoundaryError);
    };
  }, [config]);

  return <>{children}</>;
}
